import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import * as notificationService from "@/services/notificationService";
import { formatNotificationTime } from "@/services/notificationService";
import "./NotificationToastStack.css";

export default function NotificationToastStack() {
  const { t, i18n } = useTranslation();
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const unsubscribe = notificationService.subscribeToasts((newToasts) => {
      setToasts(newToasts);
    });
    return () => {
      unsubscribe();
    };
  }, []);

  if (toasts.length === 0) {
    return null;
  }

  const handleDiscuss = (toast) => {
    notificationService.markAsSeen(toast.notification_id);
    if (typeof window.triggerElelemAssistantPrompt === "function") {
      window.triggerElelemAssistantPrompt(
        t("topbar.discussNotificationPrompt", {
          message: toast.message,
          defaultValue: `Please review this notification and analyze the results: "${toast.message}"`,
        })
      );
    }
    notificationService.dismissToast(toast.id);
  };

  const handleMarkAsRead = (toast) => {
    notificationService.markAsSeen(toast.notification_id);
    notificationService.dismissToast(toast.id);
  };

  const handleDismiss = (toastId) => {
    notificationService.dismissToast(toastId);
  };

  return (
    <div
      className="elelem-toast-stack"
      aria-live="polite"
      aria-atomic="false"
      data-testid="notification-toast-stack"
      onMouseEnter={() => notificationService.pauseAllToasts()}
      onMouseLeave={() => notificationService.resumeAllToasts()}
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className="elelem-toast-card"
          role="status"
          data-testid={`notification-toast-${toast.notification_id}`}
          onMouseEnter={() => notificationService.pauseToast(toast.id)}
          onMouseLeave={() => notificationService.resumeToast(toast.id)}
        >
          <div className="elelem-toast-header">
            <div className="elelem-toast-title-wrapper">
              <span className="elelem-toast-icon-badge" aria-hidden="true">
                <i className="bi bi-bell-fill"></i>
              </span>
              <strong className="elelem-toast-title">
                {t("topbar.notifications", "Notifications")}
              </strong>
              <small className="elelem-toast-time">
                {formatNotificationTime(
                  toast.created_at,
                  t,
                  i18n.resolvedLanguage || i18n.language
                )}
              </small>
            </div>
            <button
              type="button"
              className="btn-close elelem-toast-close"
              aria-label={t("common.close", "Close")}
              onClick={() => handleDismiss(toast.id)}
            />
          </div>
          <div className="elelem-toast-body">
            <p className="elelem-toast-message mb-0">{toast.message}</p>
          </div>
          <div className="elelem-toast-actions">
            <button
              type="button"
              className="btn btn-sm elelem-toast-btn-discuss d-flex align-items-center gap-1 py-0 px-2"
              title={t("topbar.discussWithAssistant", "Discuss with AI Assistant")}
              aria-label={t("topbar.discussWithAssistant", "Discuss with AI Assistant")}
              onClick={() => handleDiscuss(toast)}
            >
              <i className="bi bi-stars"></i>
              <span>{t("topbar.discussWithAssistant", "Discuss with AI Assistant")}</span>
            </button>
            <button
              type="button"
              className="btn btn-sm btn-link elelem-toast-btn-dismiss d-flex align-items-center gap-1"
              aria-label={t("topbar.markAsRead", "Mark read")}
              onClick={() => handleMarkAsRead(toast)}
            >
              <i className="bi bi-check2"></i>
              <span>{t("topbar.markAsRead", "Mark read")}</span>
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
